import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What is Mulebuy Spreadsheet?",
    answer: "The Mulebuy Spreadsheet is a curated collection of products, sellers, and buying information designed to simplify the purchasing process. It includes verified seller listings, product links, quality check references, and shipping recommendations all organized into an easy-to-navigate format. Our team updates the spreadsheet regularly to ensure buyers always have access to the latest finds.",
  },
  {
    question: "Is the Mulebuy Spreadsheet updated regularly?",
    answer: "Yes, the Mulebuy Spreadsheet receives frequent updates. New products, seller evaluations, QC guides, and shipping information are added on an ongoing basis. The spreadsheet reflects current availability, seller performance, and trending categories so buyers always work with fresh, reliable data.",
  },
  {
    question: "Is Mulebuy safe for online shopping?",
    answer: "Mulebuy offers several buyer protection features that enhance safety. Users can further reduce risks by consulting QC information and verified seller recommendations in the Mulebuy Spreadsheet. Following our buying guides and safety tips also helps ensure a smoother transaction experience.",
  },
  {
    question: "How do I find trusted sellers on Mulebuy?",
    answer: "Our Mulebuy Spreadsheet includes a dedicated section for trusted sellers with ratings, transaction history summaries, and user feedback indicators. We evaluate sellers based on consistency, product quality, shipping reliability, and buyer reviews before including them in our recommendations.",
  },
  {
    question: "Can beginners use the Mulebuy Spreadsheet?",
    answer: "Absolutely. The Mulebuy Spreadsheet is designed to be beginner-friendly. It includes step-by-step buying guides, terminology explanations, and straightforward navigation so new users can get started without prior experience. The organized layout helps beginners find what they need without confusion.",
  },
  {
    question: "How does QC work with the Mulebuy Spreadsheet?",
    answer: "The Mulebuy Spreadsheet includes QC reference images and guidelines that help buyers evaluate product quality before committing to a purchase. Our QC guides explain what to look for in product photos, common quality indicators, and how to spot potential issues early in the process.",
  },
  {
    question: "What categories are covered in the Mulebuy Spreadsheet?",
    answer: "The spreadsheet covers an extensive range of categories including shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes, and various other items. Each category includes verified products, seller links, and relevant QC information.",
  },
  {
    question: "Is the Mulebuy Spreadsheet free to access?",
    answer: "Our website provides free access to curated category guides, buying tips, QC information, and direct links to the latest Mulebuy Spreadsheet. We aim to make high-quality buying resources accessible to everyone looking for reliable product and seller information.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="container-site">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
            Frequently Asked Questions
          </h2>
          <p className="mt-3 text-foreground-500 text-sm text-center">
            Common questions about the Mulebuy Spreadsheet
          </p>

          <div className="mt-10 space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-background-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="cursor-pointer w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                    {faq.question}
                  </span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-${openIndex === index ? "subtract" : "add"}-line text-foreground-500 text-lg transition-transform duration-200`}
                    ></i>
                  </span>
                </button>
                {openIndex === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}